import { useTranslation } from '@frontend/i18n';
import type { IconName } from '@frontend/icons';
import { getFullName } from '@frontend/name';
import { theme } from '@frontend/theme';
import { getColor } from '@frontend/design-system';
import { AvatarPill } from './avatar-pill';

export type ResourcePillType = 'unassigned' | 'archived' | 'default';
type ColorConfig = { avatarText: string; avatarFill: string; labelBackground: string };

interface Props {
  type?: ResourcePillType;
  firstName?: string;
  lastName?: string;
  name?: string;
  avatarSrc?: string;
  iconName?: IconName;
  counter?: number;
  onClick?: () => void;
  className?: string;
}

const PILL_COLORS: Partial<Record<ResourcePillType, ColorConfig>> = {
  unassigned: {
    avatarText: theme.colors.neutral70,
    avatarFill: theme.colors.neutral20,
    labelBackground: theme.colors.neutral10,
  },
  archived: {
    avatarText: theme.colors.neutral60,
    avatarFill: theme.colors.neutral10,
    labelBackground: theme.colors.neutral5,
  },
};

const getColorConfig = (colorIdentifier: string): ColorConfig => {
  const colors = getColor(colorIdentifier, false);

  if (!colors) {
    return {
      avatarText: theme.colors.neutral70,
      avatarFill: theme.colors.neutral20,
      labelBackground: theme.colors.neutral10,
    };
  }

  return {
    avatarText: colors.text,
    avatarFill: colors.fill,
    labelBackground: `color-mix(in srgb, ${colors.fill} 60%, ${theme.colors.white})`,
  };
};

export const ResourcePill = ({
  type = 'default',
  firstName,
  lastName,
  name,
  avatarSrc,
  iconName,
  counter,
  onClick,
  className,
}: Props) => {
  const { t } = useTranslation('calendar-grid');
  const fullName = getFullName({ firstName, lastName }) || name || '';

  const colors = PILL_COLORS[type] || getColorConfig(fullName || 'default');
  const avatarIconName = iconName || (type === 'unassigned' ? 'help' : type === 'archived' ? 'archive' : undefined);

  return (
    <AvatarPill
      className={className}
      labelText={type === 'archived' ? `${fullName} (${t('INACTIVE')})` : fullName}
      counter={counter}
      css={{ '> p': { backgroundColor: colors.labelBackground, color: colors.avatarText } }}
      avatarProps={{
        iconName: avatarIconName,
        firstName,
        lastName,
        name,
        src: avatarSrc,
        bypassColor: { text: colors.avatarText, fill: colors.avatarFill },
        // NOTE: for operatory pills with icons, we want to ensure the avatar has rounded corners, so we apply borderRadius to the inner div of AvatarPill
        css: iconName ? { '> div': { borderRadius: theme.borderRadius.medium } } : undefined,
      }}
      onClick={onClick}
    />
  );
};
